import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { ExamAttempt, Course, Exam, Price } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { RefreshCw, DollarSign, TrendingUp, AlertCircle } from 'lucide-react'

export function RetryEnrollmentPage() {
  useDocumentTitle('再試験申込')
  const { attemptId } = useParams<{ attemptId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null)
  const [course, setCourse] = useState<Course | null>(null)
  const [exam, setExam] = useState<Exam | null>(null)
  const [retryPrice, setRetryPrice] = useState<Price | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (!user || !attemptId) return
    fetchRetryData()
  }, [attemptId, user])

  const fetchRetryData = async () => {
    if (!attemptId || !user) return

    try {
      setLoading(true)
      setError(null)

      // 試験attempt情報を取得
      const { data: attemptData, error: attemptError } = await supabase
        .from('exam_attempts')
        .select('*, exams(*, courses(*))')
        .eq('attempt_id', attemptId)
        .eq('user_id', user.id)
        .single()

      if (attemptError || !attemptData) {
        setError('試験結果が見つかりませんでした')
        setLoading(false)
        return
      }

      // 不合格の場合のみ再受験可能
      if (attemptData.passed) {
        setError('既に合格しているため、再受験はできません')
        setLoading(false)
        return
      }

      setAttempt(attemptData)
      const examData = attemptData.exams as any
      setExam(examData)
      const courseData = examData.courses
      setCourse(courseData)

      // 再受験価格を取得
      const { data: priceData, error: priceError } = await supabase
        .from('prices')
        .select('*')
        .eq('course_id', courseData.course_id)
        .eq('price_type', 'retry')
        .single()

      if (priceError || !priceData) {
        setError('再受験の価格情報が見つかりませんでした')
        setLoading(false)
        return
      }

      setRetryPrice(priceData)
    } catch (err: any) {
      console.error('Error fetching retry data:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleRetryCheckout = async () => {
    if (!course || !retryPrice || !user || !attemptId) return

    try {
      setCreating(true)
      setError(null)

      const bffUrl = import.meta.env.VITE_BFF_URL
      const token = (await supabase.auth.getSession()).data.session?.access_token

      if (!token) {
        throw new Error('認証トークンが取得できませんでした')
      }

      const response = await fetch(`${bffUrl}/enrollment/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          course_public_key: course.course_public_key,
          is_retry: true,
          previous_attempt_id: attemptId,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || '決済セッションの作成に失敗しました')
      }

      const data = await response.json()

      // Stripe Checkoutへリダイレクト
      if (data.url) {
        window.location.href = data.url
      } else {
        throw new Error('Checkout URLが取得できませんでした')
      }
    } catch (err: any) {
      console.error('Error creating retry checkout session:', err)
      setError(err.message || '決済セッションの作成に失敗しました')
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="再受験情報を読み込み中..." />
      </MainLayout>
    )
  }

  if (error || !attempt || !course || !exam || !retryPrice) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || '再受験情報が見つかりませんでした'} />
          <div className="mt-4">
            <button
              onClick={() => navigate('/mypage')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              マイページへ戻る
            </button>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/exams/result/${attemptId}`)}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            ← 試験結果へ戻る
          </button>
        </div>

        <div className="text-center mb-8">
          <RefreshCw className="mx-auto h-12 w-12 text-blue-600 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">再受験のお申込</h1>
          <p className="text-gray-600">もう一度試験に挑戦しましょう</p>
        </div>

        {/* 前回の試験結果 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">前回の試験結果</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">試験名</span>
              <span className="font-medium">{exam.title}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">受験日</span>
              <span className="font-medium">
                {new Date(attempt.started_at).toLocaleDateString('ja-JP')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">得点</span>
              <span className="font-medium">{attempt.score || 0}点</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">合格点</span>
              <span className="font-medium">{exam.passing_score}点</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">不足点</span>
              <span className="font-medium text-red-600">
                {Math.max(0, exam.passing_score - (attempt.score || 0))}点
              </span>
            </div>
          </div>
        </div>

        {/* 改善のアドバイス */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <TrendingUp className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">合格に向けて</h3>
              <ul className="text-sm text-blue-800 space-y-2 list-disc list-inside">
                <li>講座の教材を再度復習しましょう</li>
                <li>前回間違えた問題の分野を重点的に学習しましょう</li>
                <li>理解が不十分な箇所を明確にし、補強しましょう</li>
                <li>十分に準備ができたら、再度試験に挑戦してください</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 講座復習リンク */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">講座を復習する</h2>
          <p className="text-gray-600 mb-4">
            再受験の前に、講座の内容を復習することをお勧めします
          </p>
          <button
            onClick={() => navigate(`/courses/${course.course_id}`)}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            講座ページへ
          </button>
        </div>

        {/* 再受験価格 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">再受験料</h2>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-gray-500" />
              <span className="text-lg font-medium text-gray-900">再受験料</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">
              ¥{retryPrice.amount.toLocaleString()}
            </span>
          </div>
          {retryPrice.description && (
            <p className="text-sm text-gray-600">{retryPrice.description}</p>
          )}
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* アクションボタン */}
        <div className="space-y-4">
          <button
            onClick={handleRetryCheckout}
            disabled={creating}
            className="w-full py-3 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
          >
            {creating ? '決済ページへ移動中...' : '再受験を申込む'}
          </button>

          <button
            onClick={() => navigate('/mypage')}
            disabled={creating}
            className="w-full py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
          >
            マイページへ戻る
          </button>
        </div>

        {/* 注意事項 */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">ご注意</p>
              <ul className="list-disc list-inside space-y-1">
                <li>再受験の申込後、すぐに試験を受験できます</li>
                <li>再受験は何度でも可能ですが、その都度料金が必要です</li>
                <li>前回の結果は保存されます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
